import React, { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { supabase } from '../../../supabaseClient.js';
import { formatDate } from './dashboardDisplayUtils';
import { buildHomePracticeHistory, isHomePracticeReportTitle } from './homePracticeHistory';

const cardSx = { p: 2.5, height: '100%', borderRadius: 2, border: '1px solid #E3E9EE', display: 'flex', flexDirection: 'column', gap: 1.5 };
const sourceChipSx = { height: 22, fontSize: 11, fontWeight: 600, color: '#1DA8A3', bgcolor: '#F0FDFA', border: '1px solid #B6DAD7' };

const decrypt = async value => {
  if (!value) return '';
  const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: value });
  return error ? '' : data || '';
};

const sessionDateOf = consultation => consultation.session_date || consultation.createdAt;

// session_date arrives with a migration that not every database has yet
// (see updateConsultationSessionDate), so fall back to createdAt alone.
const fetchConsultations = async patientId => {
  const withDate = await supabase.from('consultations').select('id, createdAt, session_date').eq('patient_id', patientId);
  if (!withDate.error) return withDate.data || [];
  const plain = await supabase.from('consultations').select('id, createdAt').eq('patient_id', patientId);
  return plain.error ? [] : plain.data || [];
};

// Section of the client dashboard built only from this therapist's own records,
// so it is shown whether or not the client is connected to MindVault.
// `aside` (the Risk and alerts card) sits top right, beside the client overview.
export default function ConsultationOverviewSection({ patient, aside = null }) {
  const patientId = patient?.id;
  const [loading, setLoading] = useState(false);
  const [practices, setPractices] = useState([]);
  const [sessions, setSessions] = useState({ count: 0, last: null });

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [reportsRes, consultations] = await Promise.all([
          supabase.from('generatedreports').select('id, title, created_at').eq('patientId', patientId),
          fetchConsultations(patientId),
        ]);
        const last = consultations.map(sessionDateOf).filter(Boolean).sort((a, b) => new Date(b) - new Date(a))[0] || null;
        // Titles first, so only Home practice handouts have their content decrypted.
        const reports = reportsRes.error ? [] : reportsRes.data || [];
        const titled = await Promise.all(reports.map(async report => ({ ...report, title: await decrypt(report.title) })));
        const handouts = titled.filter(report => isHomePracticeReportTitle(report.title));
        const withContent = handouts.length ? await Promise.all(handouts.map(async report => {
          const { data } = await supabase.from('generatedreports').select('content').eq('id', report.id).maybeSingle();
          return { ...report, content: await decrypt(data?.content) };
        })) : [];
        if (cancelled) return;
        setSessions({ count: consultations.length, last });
        setPractices(buildHomePracticeHistory(withContent));
      } catch (err) {
        console.error('Error loading consultation overview:', err);
        if (!cancelled) { setSessions({ count: 0, last: null }); setPractices([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [patientId]);

  const daysSince = sessions.last ? Math.max(0, Math.floor((Date.now() - new Date(sessions.last).getTime()) / 86400000)) : null;
  const diagnosis = Array.isArray(patient?.diagnosis) ? patient.diagnosis.filter(Boolean) : [];
  const concerns = (patient?.presenting_concerns || '').trim();

  return (
    <Box component="section" aria-labelledby="consultation-overview-heading" sx={{ mb: 4 }}>
      <Typography id="consultation-overview-heading" variant="subtitle1" sx={{ fontWeight: 700, color: '#14234B', mb: 1.5 }}>
        Overview
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={cardSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>Client overview</Typography>
              <Chip label="From your records" size="small" sx={sourceChipSx} />
            </Stack>
            <Box>
              <Typography variant="caption" sx={{ color: '#5B6675', fontWeight: 600 }}>Presenting concerns</Typography>
              <Typography variant="body2" sx={{ color: '#374151' }}>{concerns || 'Not recorded yet'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#5B6675', fontWeight: 600 }}>Diagnosis</Typography>
              <Typography variant="body2" sx={{ color: '#374151' }}>{diagnosis.length ? diagnosis.join(', ') : 'None recorded'}</Typography>
            </Box>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 'auto', pt: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#14234B' }}>{loading ? '–' : sessions.count}</Typography>
                <Typography variant="caption" sx={{ color: '#5B6675' }}>consultations</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#14234B' }}>{loading || !sessions.last ? '–' : formatDate(sessions.last)}</Typography>
                <Typography variant="caption" sx={{ color: '#5B6675' }}>last consultation</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#14234B' }}>{loading || daysSince === null ? '–' : `${daysSince} day${daysSince === 1 ? '' : 's'}`}</Typography>
                <Typography variant="caption" sx={{ color: '#5B6675' }}>since last consultation</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        {aside && <Grid size={{ xs: 12, md: 6 }}>{aside}</Grid>}
        <Grid size={{ xs: 12, md: aside ? 12 : 6 }}>
          <Paper elevation={0} sx={cardSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>Home practice history</Typography>
              <Chip label="From your handouts" size="small" sx={sourceChipSx} />
            </Stack>
            <Typography variant="caption" sx={{ color: '#5B6675' }}>Prescribed in your Home practice handouts</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : practices.length ? (
              <Stack spacing={1}>
                {practices.map(practice => (
                  <Stack key={`${practice.reportId}:${practice.title}`} direction="row" justifyContent="space-between" alignItems="center" spacing={1}
                    sx={{ px: 1.5, py: 1.25, border: '1px solid #E3E9EE', borderRadius: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }}>{practice.title}</Typography>
                      <Typography variant="caption" sx={{ color: '#5B6675' }}>
                        Prescribed {formatDate(practice.prescribedAt)}{practice.timing ? ` · ${practice.timing}` : ''}
                      </Typography>
                    </Box>
                    <Chip label="Prescribed" size="small" sx={{ fontWeight: 600, bgcolor: '#F1F4F7', color: '#374151' }} />
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: '#5B6675' }}>
                No home practice prescribed yet. Generate a Home practice handout from a consultation to list it here.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
